<script setup>
  import {computed, ref} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentQrCode from "@components/QrCode.vue";
  import {usePage} from "@inertiajs/vue3";
  import {recordOriginTexts} from "@pages/Records/Enums/Enums.js";
  import {MedButton, MedLink} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PatientinputClientComponentShareDialog",
  });

  const page = usePage();

  const refDialog = ref(null);

  const qrCodeDataUrl = ref(null);
  const copied = ref(false);
  const isCarer = ref(false);

  const shareLink = computed(() => (isCarer.value ? page.props.shareLinkCarer : page.props.shareLinkPatient));
  const clientCompany = computed(() => page.props.clientCompany);
  const profileEditLink = computed(() => page.props.links.p4u + "/allgemein/benutzerkonto");

  const dialogTitle = computed(() => {
    return isCarer.value ? recordOriginTexts.carerinput : recordOriginTexts.patientinput;
  });

  const downloadFileName = computed(() => {
    return isCarer.value ? "QrCodeFragebogenPflege.jpg" : "QrCodePatientenfragebogen";
  });

  const open = (carer) => {
    isCarer.value = carer;
    refDialog.value.open();
  };
  const close = () => refDialog.value.close();

  const qrCodeChanged = (dataUrl) => {
    qrCodeDataUrl.value = dataUrl;
  };

  const copyToClipboard = () => {
    try {
      navigator.clipboard.writeText(shareLink.value);
      copied.value = true;
      setTimeout(() => (copied.value = false), 1000);
    } catch (error) {
      console.warn("Failed to copy link to clipboard.", error);
    }
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog ref="refDialog" :dialog-title="dialogTitle" dialog-width="large" :test-id="getTestId('share')">
    <template #content>
      <ol class="mt-2 flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-2 lg:space-x-4">
        <li class="flex-1 rounded-md border-1 p-4 shadow-md md:p-2 md:pb-4 lg:p-4">
          <ComponentIcon custom="scan-qr-code" class="mx-auto" />
          <h3 class="text-xl font-semibold">1. Link bereitstellen</h3>
          <p class="mt-4">
            Stellen Sie
            <span v-if="isCarer">Pflegefachkräften</span>
            <span v-else>Ihren Patienten</span>
            den Link oder QR-Code zu Ihrem individuellen Fragebogen zur Verfügung, z.B. auf Ihrer Website, auf Flyern
            oder Ihrer E-Mail-Signatur.
          </p>
        </li>

        <li class="flex-1 rounded-md border-1 p-4 shadow-md md:p-2 md:pb-4 lg:p-4">
          <ComponentIcon custom="options-on-mobile" class="mx-auto" />
          <h3 class="text-xl font-semibold">2. Fragebogen befüllen</h3>
          <p class="mt-4">
            <span v-if="isCarer">Pflegefachkräfte</span>
            <span v-else>Ihre Patienten</span>
            können nun bequem alle benötigten Informationen vorab
            <span v-if="!isCarer">zu Hause</span>
            ausfüllen.
          </p>
        </li>

        <li class="flex-1 rounded-md border-1 p-4 shadow-md md:p-2 md:pb-4 lg:p-4">
          <ComponentIcon custom="front-desk" class="mx-auto" />
          <h3 class="text-xl font-semibold">3. MediCheck Analyse</h3>
          <p class="mt-4">
            Importieren Sie die bereitgestellten Daten in Ihren MediCheck und führen Sie das persönliche Gespräch
            optimal vorbereitet durch.
          </p>
        </li>
      </ol>

      <div class="mt-4 flex items-center justify-center space-x-4">
        <ComponentQrCode :value="shareLink" @change="qrCodeChanged" />

        <div class="flex flex-col space-y-4">
          <MedButton
            as="a"
            icon="save_alt"
            target="_blank"
            align="left"
            :href="qrCodeDataUrl"
            :download="downloadFileName"
            :data-test="getTestId('share-dialog-download-qr-code-data-button')"
          >
            QR-Code herunterladen
          </MedButton>

          <MedButton
            :icon="copied ? 'check' : 'content_copy'"
            :icon-filled="false"
            align="left"
            :variant="copied ? 'secondary' : 'default'"
            :data-test="getTestId('copy-sign-documents-link-to-clipboard-button')"
            @click="copyToClipboard()"
          >
            Link kopieren
          </MedButton>

          <MedButton
            as="a"
            icon="launch"
            :data-test="getTestId('open-sign-documents-button')"
            :href="shareLink"
            align="left"
            target="_blank"
          >
            Link öffnen
          </MedButton>
        </div>
      </div>

      <p class="mt-6 pl-8">
        <span v-if="clientCompany">
          <span v-if="isCarer">Pflegefachkräften</span>

          <span v-else>Ihren Patienten</span>
          wird der Name Ihrer Apotheke („{{ clientCompany }}“) angezeigt.
          <br />
          Diesen können Sie in der

          <MedLink
            variant="primary"
            :href="profileEditLink"
            target="_blank"
            :data-test="getTestId('share-dialog-profile-edit-link')"
          >
            pharma4u-Benutzerverwaltung
          </MedLink>
          ändern.
        </span>

        <span v-else>
          Bitte hinterlegen Sie vorab Ihren Apothekennamen in der
          <MedLink
            variant="primary"
            :href="profileEditLink"
            target="_blank"
            :data-test="getTestId('share-dialog-profile-edit-link')"
          >
            pharma4u-Benutzerverwaltung.
          </MedLink>
        </span>
      </p>

      <div class="mt-4 flex space-x-2">
        <ComponentIcon class="text-2xl text-blue-600">info</ComponentIcon>

        <p class="text-blue-700">
          Bitte beachten: Ihre Apotheke ist im Sinne der DSGVO verantwortlich für die Datenverarbeitung. Wir empfehlen,
          Ihren Patienten Ihre eigene Datenschutzerklärung z.B. per Link zugänglich zu machen. Eine allgemeingültige
          Vorlage können Sie sich

          <MedLink
            variant="primary"
            href="/static/Mustereinwilligungserklärung-Medikationsanalyse.docx"
            target="_blank"
            :data-test="getTestId('share-dialog-download-declaration-of-consent-link')"
          >
            hier herunterladen
          </MedLink>
          (Word-Datei im docx-Format).
        </p>
      </div>
    </template>

    <template #actions>
      <MedButton :data-test="getTestId('share-dialog-close-button')" @click="close()">Schließen</MedButton>
    </template>
  </ComponentDialog>
</template>
